/**
 * skinResolutionMixin — shared skin-resolution infrastructure.
 *
 * Implements Skin Resolution Contract v1.1 against the actual RunsNative
 * code vocabulary (`run-*` / `RunElement` / `data-run-skin`). The contract
 * document still uses the stale `yensid-*` / `data-ys-skin` identifiers; this
 * code is authoritative — see RUN-379 and the contract-drift docs flag.
 *
 * What this replaces: every multi-skin component used to hand-copy a private
 * `#resolveSkin()` plus a hardcoded `if (skin === 'wireframe') … else …`
 * ladder in `updated()`, with one constructable stylesheet per skin created
 * via static import. That is the import-time skin-selection anti-pattern the
 * contract abolishes. This mixin centralises resolution, lazy loading, the
 * two-layer `adoptedStyleSheets` model, and the render delegation.
 *
 * Component contract (what a migrated component declares):
 *
 *   import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
 *   import * as defaultSkin from './skins/foo.default.js';
 *
 *   class RunFoo extends skinResolutionMixin(RunElement) {
 *     static defaultSkin = defaultSkin;           // always bundled, sync
 *     static skinLoaders = {                      // all others lazy
 *       wireframe: () => import('./skins/foo.wireframe.js'),
 *     };
 *     // render() is no longer implemented for skin output; the mixin's
 *     // _render() delegates to the resolved skin module. Components keep
 *     // post-skin DOM work (ARIA forwarding, slot flags, boundary) in updated().
 *   }
 *
 * A skin module is `{ styles: string, render(host): void }` exposed either as
 * named exports (the existing `foo.default.js` / `foo.wireframe.js` shape) or
 * as a default export (the shape in the contract's examples). Both are
 * normalised here so neither authoring convention is privileged.
 */

const SKIN_BRAND = Symbol('runSkinResolved');

// Per-class state. Keyed by the concrete component class so subclasses never
// share a cache through the prototype chain (every class has its own
// `defaultSkin` / `skinLoaders`). A null module value is a *negative* cache
// entry (unknown / failed / malformed) — it makes the fallback path stop
// re-importing and, critically, stop the requestUpdate re-render loop.
const _classState = new WeakMap();

function stateFor(cls) {
  let s = _classState.get(cls);
  if (!s) {
    s = {
      mods: new Map(),       // skinName -> normalized module | null (negative)
      skinSheets: new Map(), // skinName -> CSSStyleSheet
      baseSheet: undefined,  // CSSStyleSheet | null (resolved lazily)
    };
    _classState.set(cls, s);
  }
  return s;
}

/**
 * Accept a skin module exported as named members OR as a default export OR a
 * frozen `defineSkin()` object. Returns `{ styles, render, manifest?,
 * transforms? }` or null if the shape is invalid.
 */
function normalizeSkinModule(mod) {
  if (!mod) return null;
  const candidate =
    typeof mod.render === 'function' ? mod
    : mod.default ? mod.default
    : mod;
  if (!candidate) return null;
  if (typeof candidate.styles !== 'string') return null;
  if (typeof candidate.render !== 'function') return null;
  return candidate;
}

function baseSheetFor(cls) {
  const s = stateFor(cls);
  if (s.baseSheet !== undefined) return s.baseSheet;
  // Headless base styles are optional. Today all CSS lives in the skin module
  // (skin owns 100% of styles), so this is usually null — but the two-layer
  // model is honoured if a headless base ever declares `static styles`.
  const css = cls.styles;
  if (typeof css === 'string' && css.length > 0) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    s.baseSheet = sheet;
  } else {
    s.baseSheet = null;
  }
  return s.baseSheet;
}

function skinSheetFor(cls, skinName, skinModule) {
  const s = stateFor(cls);
  let sheet = s.skinSheets.get(skinName);
  if (!sheet) {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(skinModule.styles || '');
    s.skinSheets.set(skinName, sheet);
  }
  return sheet;
}

export function skinResolutionMixin(BaseClass) {
  class SkinResolved extends BaseClass {
    /**
     * Default skin module — set by the component. Always statically imported
     * so it is bundled and resolvable with zero async (no blank flash).
     * @type {object}
     */
    static defaultSkin = null;

    /**
     * Lazy registry: `{ [skinName]: () => Promise<module> }`. The default skin
     * is NOT listed here (it is `static defaultSkin`). Asset-heavy skins must
     * route through `loadSkinModule()` from skin-loader.js to preserve the
     * Vite pre-bundling workaround.
     * @type {Object.<string, () => Promise<any>>}
     */
    static skinLoaders = {};

    get [SKIN_BRAND]() { return true; }

    /**
     * Resolve the active skin name via the four-level cascade. First match
     * wins. Crosses shadow-DOM boundaries via getRootNode().host so a
     * component nested inside another component's shadow tree still sees skin
     * context set outside that boundary.
     *
     * 1 (highest) instance `skin` property/attribute
     * 2           nearest ancestor `data-run-skin` (composed tree)
     * 3           document.documentElement `data-run-skin`
     * 4 (lowest)  "default"
     */
    _resolveSkinName() {
      const own =
        (typeof this.skin === 'string' && this.skin) ||
        this.getAttribute('skin');
      if (own) return own;

      let node = this;
      // Walk up the composed tree, hopping shadow boundaries at the root.
      while (node) {
        let parent = node.parentElement;
        if (!parent) {
          const root = node.getRootNode && node.getRootNode();
          if (root && root.host) {
            node = root.host;
            continue;
          }
          break;
        }
        while (parent) {
          const ctx = parent.getAttribute && parent.getAttribute('data-run-skin');
          if (ctx) return ctx;
          parent = parent.parentElement;
        }
        // Reached the top of this tree; try to hop a shadow boundary.
        const root = node.getRootNode && node.getRootNode();
        node = root && root.host ? root.host : null;
      }

      const docCtx =
        typeof document !== 'undefined' &&
        document.documentElement &&
        document.documentElement.getAttribute('data-run-skin');
      if (docCtx) return docCtx;

      return 'default';
    }

    /**
     * Load (and cache, per class) a skin module. Returns the normalized
     * module, or null for unknown / failed / malformed skins. Failures are
     * negatively cached so the render fallback never re-imports or loops.
     */
    static async loadSkin(skinName) {
      if (!skinName || skinName === 'default') {
        return normalizeSkinModule(this.defaultSkin);
      }
      const s = stateFor(this);
      if (s.mods.has(skinName)) return s.mods.get(skinName);

      const loader = this.skinLoaders && this.skinLoaders[skinName];
      if (typeof loader !== 'function') {
        console.warn(
          `[skin-resolution] no loader registered for skin "${skinName}" on ` +
          `<${this.componentTag || (this.name || 'component')}>; using default`,
        );
        s.mods.set(skinName, null);
        return null;
      }
      try {
        const normalized = normalizeSkinModule(await loader());
        if (!normalized) {
          console.error(
            `[skin-resolution] skin module "${skinName}" is malformed ` +
            `(missing string \`styles\` or function \`render\`); using default`,
          );
          s.mods.set(skinName, null);
          return null;
        }
        s.mods.set(skinName, normalized);
        return normalized;
      } catch (err) {
        console.error(
          `[skin-resolution] failed to load skin "${skinName}"; using default`,
          err,
        );
        s.mods.set(skinName, null);
        return null;
      }
    }

    /** Per-component preload convenience (eliminates the default→skin flash). */
    static preloadSkin(skinName) {
      return this.loadSkin(skinName);
    }

    /** Force this instance to re-resolve and re-render its skin. */
    refreshSkin() {
      return this.requestUpdate();
    }

    /**
     * Mixin _render override (Skin Resolution Contract §"Mixin's _render
     * Override"): skip the headless `<style>` injection, resolve the skin,
     * manage adoptedStyleSheets, delegate DOM to the skin module. Runs once
     * per update cycle (before `updated()`), so a component's post-skin DOM
     * work in `updated()` still observes the freshly rendered skin DOM.
     */
    _render() {
      if (!this.shadowRoot) return;
      const cls = this.constructor;
      const resolved = this._resolveSkinName();

      let skinModule;
      let appliedName;

      if (resolved === 'default') {
        skinModule = normalizeSkinModule(cls.defaultSkin);
        appliedName = 'default';
      } else {
        const state = stateFor(cls);
        if (state.mods.has(resolved)) {
          const cached = state.mods.get(resolved);
          if (cached) {
            skinModule = cached;
            appliedName = resolved;
          } else {
            // Negatively cached (unknown/failed/malformed) — silent default.
            skinModule = normalizeSkinModule(cls.defaultSkin);
            appliedName = 'default';
          }
        } else {
          // Not loaded yet: render default now, kick the import, re-render
          // only on success. loadSkin negatively caches failures so the
          // .then re-render cannot loop.
          cls.loadSkin(resolved).then((mod) => {
            if (mod && this.isConnected) this.requestUpdate();
          });
          skinModule = normalizeSkinModule(cls.defaultSkin);
          appliedName = 'default';
        }
      }

      if (!skinModule) {
        // Default skin itself is missing/malformed — there is no safe
        // fallback. Surface loudly; do not throw (would desync updateComplete).
        console.error(
          `[skin-resolution] <${this.localName}> has no valid default skin; ` +
          `nothing rendered. Set \`static defaultSkin\`.`,
        );
        this._resolvedSkinName = 'default';
        return;
      }

      const baseSheet = baseSheetFor(cls);
      const skinSheet = skinSheetFor(cls, appliedName, skinModule);
      this.shadowRoot.adoptedStyleSheets = baseSheet
        ? [baseSheet, skinSheet]
        : [skinSheet];

      this._resolvedSkinName = appliedName;
      // For a future manifest consumer (skin-theme-boundary-contract-v1):
      // the resolved module is exposed but not yet interpreted here.
      this._resolvedSkinModule = skinModule;

      skinModule.render(this);
    }

    /** The resolved skin module (incl. optional `manifest`/`transforms`). */
    getResolvedSkinModule() {
      return this._resolvedSkinModule || null;
    }
  }
  return SkinResolved;
}

/**
 * Bulk preload convenience — iterates skin-resolved custom elements currently
 * in the document (light + shadow trees) and preloads `skinName` on each
 * class that registers a loader for it. Best-effort: the deterministic path
 * is per-class `Component.loadSkin(name)`. Mirrors the contract's
 * "iterates registered elements" semantics.
 */
export async function preloadSkin(skinName, root = (typeof document !== 'undefined' ? document : null)) {
  if (!root || skinName === 'default') return;
  const classes = new Set();
  for (const el of _walkSkinned(root)) classes.add(el.constructor);
  await Promise.all(
    [...classes]
      .filter((c) => typeof c.loadSkin === 'function')
      .map((c) => c.loadSkin(skinName)),
  );
}

/**
 * Force re-resolution across a subtree after an ancestor `data-run-skin`
 * changed (v1 is not MutationObserver-reactive — see contract §Reactivity).
 */
export function refreshSkinContext(rootEl) {
  if (!rootEl) return;
  for (const el of _walkSkinned(rootEl)) {
    if (typeof el.refreshSkin === 'function') el.refreshSkin();
  }
}

function* _walkSkinned(root) {
  const start =
    root.shadowRoot ? root.shadowRoot
    : root.documentElement ? root.documentElement
    : root;
  const stack = [start];
  while (stack.length) {
    const node = stack.pop();
    const kids = node.querySelectorAll ? node.querySelectorAll('*') : [];
    for (const el of kids) {
      if (el[SKIN_BRAND]) yield el;
      if (el.shadowRoot) stack.push(el.shadowRoot);
    }
  }
}

/** @typedef {{styles: string, render: (host: HTMLElement) => void, manifest?: object, transforms?: Object.<string, (v: string) => string>}} SkinModule */
